<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Bank Accounts</a></li>
                    </ul>

                    <h3>Manage Bank Accounts</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewBankAccount" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Bank Account</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="bankAccounts"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['bankName', 'accountHolder', 'accountNumber']">

                        <template #header>
                            <div class="p-grid">
                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedColumns" :options="columnsFullList"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleColumns"
                                                     display="chip" placeholder="Select Columns"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <MultiSelect :modelValue="selectedStandardFilter" :options="standardFilters"
                                                     optionLabel="header"
                                                     @update:modelValue="onToggleStandardFilters"
                                                     display="chip" placeholder="Select Standard Filter"/>
                                    </div>
                                </div>

                                <div class="grid">
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-filter-slash" label="Clear Search"
                                                class="p-button-outlined p-ml-2" @click="clearFilter()"/>
                                    </div>
                                    <div class="col-12 lg:col-6 p-2">
                                        <Button type="button" icon="pi pi-replay" label="Restore to Original View"
                                                class="p-button-outlined" @click="restoreToOriginal()"/>
                                    </div>
                                </div>

                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Bank Name, Account Holder or Account Number"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No bank accounts found.
                        </template>

                        <template #loading>
                            Loading bank accounts. Please wait.
                        </template>

                        <Column field="selectAnOption" style="width: 10%">
                            <template #body="{ data }">
                                <SplitButton icon="pi pi-pencil" label="Select" :model="getFilteredItems(data)"
                                             severity="success"
                                             @click="chooseBlankSelection(data)" class="mb-2 mr-2 ml-2 mt-2"/>
                            </template>
                        </Column>

                        <template v-for="col in selectedColumns" :key="col.field">

                            <Column v-if="col.field === 'status'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Status</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                </template>
                            </Column>

                            <Column v-else-if="col.field === 'accountType'" :field="col.field" :header="col.header"
                                    :sortable="col.sortable">
                                <template #body="{data}">
                                    <span class="p-column-title">Account Type</span>
                                    <span v-if="data.accountType === 'C'"
                                          :class="'type-badge status-' + data.accountType">Current</span>
                                    <span v-if="data.accountType === 'S'"
                                          :class="'type-badge status-' + data.accountType">Savings</span>
                                    <span v-if="data.accountType === 'I'"
                                          :class="'type-badge status-' + data.accountType">Investments</span>
                                </template>
                            </Column>

                            <Column v-else :field="col.field" :header="col.header" :sortable="col.sortable"/>

                        </template>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Bank Account {{ this.bankAccount.bankName }}</h3>

                    <div class="field">
                        <label for="accountHolder">Account Holder</label>
                        <InputText id="accountHolder" v-model.trim="bankAccount.accountHolder" required="true"/>
                    </div>

                    <div class="field">
                        <label for="bankName">Bank Name</label>
                        <Dropdown v-model="bankAccount.bankName" :options="bankNames" optionLabel="name" optionValue="code"
                                  :editable="true" placeholder="Select a Bank Name"/>
                    </div>

                    <div class="field">
                        <label for="branchName">Branch Name</label>
                        <InputText id="branchName" v-model.trim="bankAccount.branchName" required="true"/>
                    </div>

                    <p>{{ companyConfiguration.region }}</p>

                    <div v-if="companyConfiguration.region === 'ZA'" class="field">
                        <label for="branchCode">Branch Code</label>
                        <InputText id="branchCode" v-model.trim="bankAccount.branchCode" required="true"/>
                    </div>

                    <div v-if="companyConfiguration.region !== 'ZA'" class="field">
                        <label for="branchCode">Sort Code</label>
                        <InputText id="branchCode" v-model.trim="bankAccount.branchCode" required="true"/>
                    </div>

                    <div class="field">
                        <label for="accountType">Account Type</label>
                        <Dropdown v-model="bankAccount.accountType" :options="accountTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select an Account Type"/>
                    </div>

                    <div class="field">
                        <label for="accountNumber">Account Number</label>
                        <InputText id="accountNumber" v-model.trim="bankAccount.accountNumber" required="true"/>
                    </div>

                    <div class="field">
                        <label for="swiftCode">Swift Code</label>
                        <InputText id="swiftCode" v-model.trim="bankAccount.swiftCode" required="true"/>
                    </div>

                    <div class="field">
                        <label for="bankAddress">Bank Address</label>
                        <InputText id="bankAddress" v-model.trim="bankAccount.bankAddress" required="true"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateBankAccount"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Bank Account {{ this.bankAccount.bankName }}</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Bank Account?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteBankAccount"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Create Bank Account</h3>

                    <div class="field">
                        <label for="accountHolder">Account Holder</label>
                        <InputText id="accountHolder" v-model.trim="newBankAccount.accountHolder" required="true"/>
                    </div>

                    <div class="field">
                        <label for="bankName">Bank Name</label>
                        <Dropdown v-model="newBankAccount.bankName" :options="bankNames" optionLabel="name" optionValue="code"
                                  :editable="true" placeholder="Select a Bank Name"/>
                    </div>

                    <div class="field">
                        <label for="branchName">Branch Name</label>
                        <InputText id="branchName" v-model.trim="newBankAccount.branchName" required="true"/>
                    </div>

                    <div v-if="companyConfiguration.region === 'ZA'" class="field">
                        <label for="branchCode">Branch Code</label>
                        <InputText id="branchCode" v-model.trim="newBankAccount.branchCode" required="true"/>
                    </div>

                    <div v-if="companyConfiguration.region !== 'ZA'" class="field">
                        <label for="branchCode">Sort Code</label>
                        <InputText id="branchCode" v-model.trim="newBankAccount.branchCode" required="true"/>
                    </div>

                    <div class="field">
                        <label for="accountType">Account Type</label>
                        <Dropdown v-model="newBankAccount.accountType" :options="accountTypes" optionLabel="name" optionValue="code"
                                  placeholder="Select an Account Type"/>
                    </div>

                    <div class="field">
                        <label for="accountNumber">Account Number</label>
                        <InputText id="accountNumber" v-model.trim="newBankAccount.accountNumber" required="true"/>
                    </div>

                    <div class="field">
                        <label for="swiftCode">Swift Code</label>
                        <InputText id="swiftCode" v-model.trim="newBankAccount.swiftCode" required="true"/>
                    </div>

                    <div class="field">
                        <label for="bankAddress">Bank Address</label>
                        <InputText id="bankAddress" v-model.trim="newBankAccount.bankAddress" required="true"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createBankAccount"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import CompanyService from "@/service/CompanyService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import BankAccountService from "@/service/BankAccountService";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();

        this.standardFilters = [
            {field: 'active', header: 'Active'},
            {field: 'inactive', header: 'Inactive'}
        ];

    },
    async mounted() {
        await this.loadCompanyContext();
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filters: null,
            loading: true,
            submitted: false,
            filteredItemsCache: new Map(),
            bankAccount: {},
            bankAccounts: null,
            newBankAccount: {},
            originalBankAccounts: null,
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ],
            bankNamesSouthAfrica: [
                {name: 'ABSA', code: 'ABSA'},
                {name: 'Capitec', code: 'Capitec'},
                {name: 'First National Bank', code: 'First National Bank'},
                {name: 'Investec', code: 'Investec'},
                {name: 'Nedbank', code: 'Nedbank'},
                {name: 'Standard Bank', code: 'Standard Bank'},
                {name: 'Mercentile Bank', code: 'Mercentile Bank'},
                {name: 'Discovery Bank', code: 'Discovery Bank'}
            ],
            bankNamesUK: [
                {name: 'HSBC', code: 'HSBC'},
                {name: 'Barclays', code: 'Barclays'},
                {name: 'Lloyds', code: 'Lloyds'},
                {name: 'JP Morgan', code: 'JP Morgan'},
                {name: 'Morgan Stanley', code: 'Morgan Stanley'},
                {name: 'Standard Chartered', code: 'Standard Chartered'},
                {name: 'Citibank', code: 'Citibank'},
                {name: 'Santander', code: 'Santander'},
                {name: 'Monzo', code: 'Monzo'},
                {name: 'Revolut', code: 'Revolut'},
                {name: 'Starling', code: 'Starling'},
                {name: 'Metro', code: 'Metro'},
                {name: 'Marks and Spencer', code: 'Marks and Spencer'},
                {name: 'Sainsbury', code: 'Sainsbury'},
                {name: 'Natwest', code: 'Natwest'},
                {name: 'Royal Bank of Scotland', code: 'Royal Bank of Scotland'},
            ],
            bankNames: [],
            accountTypes: [
                {name: 'Current', code: 'C'},
                {name: 'Savings', code: 'S'},
                {name: 'Investment', code: 'I'},
            ]
        }
    },
    watch: {
        bankAccounts() {
            this.filteredItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id)
                ]);

                this.userConfiguration = userConfigData.data;
                console.log("[PhoneCalls][mounted] userConfigData.data = ", userConfigData.data);
            } catch (e) {
                console.error("[PhoneCalls][mounted] Could not find user config for user = ", this.userStore.user.id);
            }

            try {
                const [bankAccountData, companyConfigData] = await Promise.all([
                    BankAccountService.getBankAccountsForCompany(this.profile.companyId),
                    CompanyService.getCompanyConfiguration(this.profile.companyId)
                ]);

                this.bankAccounts = bankAccountData.data;
                this.originalBankAccounts = bankAccountData.data;
                console.log("[PhoneCalls][mounted] bankAccountData.data = ", bankAccountData.data);

                this.companyConfiguration = companyConfigData.data;
                console.log("[PhoneCalls][mounted] companyConfigData.data = ", companyConfigData.data);

                if (this.companyConfiguration.region === "ZA") {
                    this.bankNames = this.bankNamesSouthAfrica;

                    this.columnsDefault = [
                        {field: 'bankName', header: 'Bank Name', sortable: true},
                        {field: 'accountHolder', header: 'Account Holder', sortable: true},
                        {field: 'accountNumber', header: 'Account Number', sortable: true},
                        {field: 'accountType', header: 'Account Type', sortable: true},
                        {field: 'status', header: 'Status', sortable: true}
                    ];

                    this.columnsFullList = [
                        {field: 'bankName', header: 'Bank Name', sortable: true},
                        {field: 'accountHolder', header: 'Account Holder', sortable: true},
                        {field: 'accountNumber', header: 'Account Number', sortable: true},
                        {field: 'accountType', header: 'Account Type', sortable: true},
                        {field: 'status', header: 'Status', sortable: true},
                        {field: 'branchName', header: 'Branch Name', sortable: true},
                        {field: 'branchCode', header: 'Branch Code', sortable: true},
                        {field: 'swiftCode', header: 'Swift Code', sortable: true},
                        {field: 'bankAddress', header: 'Bank Address', sortable: true}
                    ];

                } else {
                    this.bankNames = this.bankNamesUK;

                    this.columnsDefault = [
                        {field: 'bankName', header: 'Bank Name', sortable: true},
                        {field: 'accountHolder', header: 'Account Holder', sortable: true},
                        {field: 'accountNumber', header: 'Account Number', sortable: true},
                        {field: 'accountType', header: 'Account Type', sortable: true},
                        {field: 'status', header: 'Status', sortable: true}
                    ];

                    this.columnsFullList = [
                        {field: 'bankName', header: 'Bank Name', sortable: true},
                        {field: 'accountHolder', header: 'Account Holder', sortable: true},
                        {field: 'accountNumber', header: 'Account Number', sortable: true},
                        {field: 'accountType', header: 'Account Type', sortable: true},
                        {field: 'status', header: 'Status', sortable: true},
                        {field: 'branchName', header: 'Branch Name', sortable: true},
                        {field: 'branchCode', header: 'Sort Code', sortable: true},
                        {field: 'swiftCode', header: 'Swift Code', sortable: true},
                        {field: 'bankAddress', header: 'Bank Address', sortable: true}
                    ];
                }

                this.selectedColumns = this.columnsDefault;

                this.loading = false;

            } catch (e) {
                console.error("[PhoneCalls][mounted] Error fetching additional data = ", e);
            }

            console.log("[PhoneCalls][mounted] this.bankAccounts = ", this.bankAccounts);
            console.log("[PhoneCalls][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[PhoneCalls][mounted] this.companyConfiguration = ", this.companyConfiguration);
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'bankName': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'accountHolder': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'accountNumber': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewBankAccount() {
            this.newBankAccount = {};
            this.newBankAccount.accountType = 'C';
            this.activeTab = 4;
        },
        callHelp() {
            console.log('[ConfigurationBilling][callHelp] Start.');
        },
        callSupport() {
            console.log('[ConfigurationBilling][callSupport] Start.');
        },
        callUpdateBankAccount(data) {
            this.activeTab = 2;
            this.bankAccount = data;
        },
        callDeleteBankAccount(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.bankAccount = data;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.bankAccounts !== null) {

                if (isActive) {
                    this.bankAccounts = this.originalBankAccounts.filter(bankAccount => bankAccount.status === 'A');
                } else if (isInactive) {
                    this.bankAccounts = this.originalBankAccounts.filter(bankAccount => bankAccount.status === 'I');
                } else {
                    this.bankAccounts = this.originalBankAccounts;
                }

            } else {
                // Handle the case when auditTrails is null
                this.bankAccounts = null;
            }
        },
        getFilteredItems(data) {
            if (this.filteredItemsCache.has(data.id)) {
                return this.filteredItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateBankAccount(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteBankAccount(data);
                }
            });

            this.filteredItemsCache.set(data.id, items);

            return items;
        },
        chooseBlankSelection(data) {
            console.log('[PhoneCalls][chooseBlankSelection] data = ', data);
            this.$toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid option.',
                life: 3000
            });
        },
        async createBankAccount() {
            this.submitted = true;

            if (this.newBankAccount.accountHolder) {

                try {
                    var bankAccountDto = {
                        bankName: this.newBankAccount.bankName,
                        accountHolder: this.newBankAccount.accountHolder,
                        branchName: this.newBankAccount.branchName,
                        branchCode: this.newBankAccount.branchCode,
                        accountType: this.newBankAccount.accountType,
                        accountNumber: this.newBankAccount.accountNumber,
                        swiftCode: this.newBankAccount.swiftCode,
                        bankAddress: this.newBankAccount.bankAddress,
                        companyId: this.profile.companyId,
                        status: 'A'
                    };

                    const response = await BankAccountService.createBankAccount(this.profile.companyId, bankAccountDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [bankAccountData] = await Promise.all([
                        BankAccountService.getBankAccountsForCompany(this.profile.companyId)
                    ]);

                    this.bankAccounts = bankAccountData.data;
                    this.originalBankAccounts = bankAccountData.data;
                    console.log("[PhoneCalls][mounted] bankAccountData.data = ", bankAccountData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created bank account',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create bank account. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateBankAccount() {
            this.submitted = true;

            if (this.bankAccount.id) {

                try {
                    var bankAccountDto = {
                        id: this.bankAccount.id,
                        bankName: this.bankAccount.bankName,
                        accountHolder: this.bankAccount.accountHolder,
                        branchName: this.bankAccount.branchName,
                        branchCode: this.bankAccount.branchCode,
                        accountType: this.bankAccount.accountType,
                        accountNumber: this.bankAccount.accountNumber,
                        swiftCode: this.bankAccount.swiftCode,
                        bankAddress: this.bankAccount.bankAddress,
                        companyId: this.bankAccount.companyId,
                        status: this.bankAccount.status,
                    };

                    const response = await BankAccountService.updateBankAccount(this.bankAccount.id, bankAccountDto);
                    console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                    const [bankAccountData] = await Promise.all([
                        BankAccountService.getBankAccountsForCompany(this.profile.companyId)
                    ]);

                    this.bankAccounts = bankAccountData.data;
                    this.originalBankAccounts = bankAccountData.data;
                    console.log("[PhoneCalls][mounted] bankAccountData.data = ", bankAccountData.data);

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated bank account',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update bank account. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteBankAccount() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.bankAccount.id) {

                    try {
                        const response = await BankAccountService.deleteBankAccount(this.bankAccount.id);
                        console.log('[PhoneCalls][deletePhoneCall] Response = ', response);

                        const [bankAccountData] = await Promise.all([
                            BankAccountService.getBankAccountsForCompany(this.profile.companyId)
                        ]);

                        this.bankAccounts = bankAccountData.data;
                        this.originalBankAccounts = bankAccountData.data;
                        console.log("[PhoneCalls][mounted] bankAccountData.data = ", bankAccountData.data);

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted bank account',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[PhoneCalls][deletePhoneCall] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete bank account. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    },
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .type-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #9da5d9;
        color: #080c61;
    }

    &.status-I {
        background: #B3E5FC;
        color: #23547B;
    }

}

.type-badge {
    &.status-C {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-S {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-I {
        background: #bc91d5;
        color: #71038f;
    }

}

.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
